/**
 * The one HTTP client for the MOVO API.
 *
 * - Real requests only in the live app. There is no mock fallback: when the server is unreachable or
 *   returns an error the caller gets a typed `ApiError` and the screen shows it.
 * - The one exception is the demo build (`VITE_APP_MODE=demo`): every call is answered in the browser by
 *   `src/demo/` and nothing leaves the page. The engine is imported lazily behind `IS_DEMO`, a build-time
 *   constant, so the live bundle contains none of it.
 * - The access token comes from the auth layer through `configureApi`, never from a
 *   component. A 401 signs the user out.
 * - Request bodies are plain JSON. Money amounts are sent as rupee numbers; the server
 *   does all arithmetic.
 */

import { API_URL, IS_DEMO } from '../env';

const DEFAULT_BASE_URL = '/api/v1';
const REQUEST_TIMEOUT_MS = 20_000;

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;

export interface RequestOptions {
  query?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Sent as `Idempotency-Key` so a retried money request cannot be applied twice. */
  idempotencyKey?: string;
}

export type ApiErrorKind = 'network' | 'timeout' | 'unauthorized' | 'forbidden' | 'not_found' | 'validation' | 'conflict' | 'server' | 'unknown';

export class ApiError extends Error {
  readonly status: number;
  readonly kind: ApiErrorKind;
  /** Field-level or per-rule messages returned by server validation. */
  readonly details: string[];

  constructor(message: string, status: number, kind: ApiErrorKind, details: string[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
    this.details = details;
  }

  get isRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || this.kind === 'server';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

interface ApiConfig {
  getToken: () => string | null;
  onUnauthorized: () => void;
}

let config: ApiConfig = { getToken: () => null, onUnauthorized: () => undefined };

export function configureApi(next: Partial<ApiConfig>): void {
  config = { ...config, ...next };
}

export function apiBaseUrl(): string {
  return (API_URL ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
}

function buildUrl(path: string, query?: QueryParams): string {
  const url = `${apiBaseUrl()}/${path.replace(/^\/+/, '')}`;
  if (!query) {
    return url;
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) {
    return 'unauthorized';
  }
  if (status === 403) {
    return 'forbidden';
  }
  if (status === 404) {
    return 'not_found';
  }
  if (status === 409) {
    return 'conflict';
  }
  if (status === 400 || status === 422) {
    return 'validation';
  }
  return status >= 500 ? 'server' : 'unknown';
}

/** Nest returns `{ message: string | string[], error, statusCode }`. */
function readErrorBody(payload: unknown, fallback: string): { message: string; details: string[] } {
  if (payload && typeof payload === 'object' && 'message' in payload) {
    const raw = (payload as { message: unknown }).message;
    if (Array.isArray(raw)) {
      const details = raw.filter((item): item is string => typeof item === 'string');
      return { message: details[0] ?? fallback, details };
    }
    if (typeof raw === 'string' && raw.trim()) {
      return { message: raw, details: [raw] };
    }
  }
  return { message: fallback, details: [] };
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), REQUEST_TIMEOUT_MS);
  const onCallerAbort = (): void => controller.abort(options.signal?.reason);
  options.signal?.addEventListener('abort', onCallerAbort, { once: true });

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  const token = config.getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  const hasBody = options.body !== undefined;
  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }

  let status: number;
  let statusText = '';
  let payload: unknown;
  if (IS_DEMO) {
    // Same error handling as a real response, but the "server" is the in-browser demo engine.
    try {
      const { handleDemoRequest } = await import('@/demo');
      const reply = await handleDemoRequest({ method, path, query: options.query, body: options.body, token, signal: options.signal });
      status = reply.status;
      payload = reply.body;
    } finally {
      window.clearTimeout(timeout);
      options.signal?.removeEventListener('abort', onCallerAbort);
    }
  } else {
    let response: Response;
    try {
      response = await fetch(buildUrl(path, options.query), {
        method,
        headers,
        body: hasBody ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      });
    } catch (error) {
      if (options.signal?.aborted) {
        throw error;
      }
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new ApiError('The server took too long to respond. Please try again.', 0, 'timeout');
      }
      throw new ApiError("Can't reach the MOVO server. Check your connection and try again.", 0, 'network');
    } finally {
      window.clearTimeout(timeout);
      options.signal?.removeEventListener('abort', onCallerAbort);
    }
    status = response.status;
    statusText = response.statusText;
    payload = await parseBody(response);
  }

  if (status < 200 || status >= 300) {
    const kind = kindForStatus(status);
    const { message, details } = readErrorBody(payload, statusText || 'Request failed');
    if (kind === 'unauthorized') {
      config.onUnauthorized();
    }
    throw new ApiError(message, status, kind, details);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => request<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => request<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('DELETE', path, options),
};

/** A fresh key per user intent (one click of "Collect"), reused across automatic retries. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
